import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  LabVisitsService,
  type ConsultationNote,
  type ConsultationNoteCreate,
} from "@/lib/api_client";
import { useAuth } from "@/lib/auth";
import { impersonateHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

// Consultation notes are journal text: the API answers only an impersonation
// token. Both callers mount under the anamnesis page's impersonation gate, so
// the ambient token is the one for the patient whose visit is on screen.
export const useConsultationNotes = (labVisitId: string | null | undefined) => {
  const { impersonateToken } = useAuth();
  return useQuery({
    queryKey: ["consultation-notes", labVisitId],
    queryFn: () =>
      fetchAll<ConsultationNote>((opts) =>
        LabVisitsService.getApiConsultationNotes({
          ...opts,
          query: { ...opts?.query, "filter[labVisitId]": labVisitId! },
          headers: impersonateHeaders(impersonateToken!, opts.headers),
        }),
      ),
    enabled: !!impersonateToken && !!labVisitId,
  });
};

export const useCreateConsultationNote = () => {
  const qc = useQueryClient();
  const { impersonateToken } = useAuth();
  return useMutation({
    mutationFn: async (data: ConsultationNoteCreate) => {
      if (!impersonateToken) throw new Error("Ikke impersonert");
      const res = await LabVisitsService.postApiConsultationNotes({
        body: data,
        headers: impersonateHeaders(impersonateToken),
      });
      if (!res.data) throw new Error("Failed to create consultation note");
      return res.data as unknown as ConsultationNote;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["consultation-notes", variables.labVisitId],
      });
    },
  });
};
